<script setup lang="ts">
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'

  defineProps<{
    label: string
    text: string
    textAlignment?: string
    width?: string
  }
  >()

  // The ibexa selection type returns the index of the selected option.
  // These functions map the index to a more readable class name for text alignment and width.
  const mapTextAlignment = (alignment?: string) => alignment === '0' ? 'left' : 'center'
  const mapWidth = (width?: string) => width === '0' ? 'auto' : 'full'
</script>

<template>
  <figure :class="['highlighted-box', mapWidth(width)]">
    <figcaption class="label">
      {{ label }}
    </figcaption>
    <div :class="['content', mapTextAlignment(textAlignment)]">
      <RichTextRenderer :rich-text="text" />
    </div>
  </figure>
</template>

<style lang="scss" scoped>
  $highlight-color: var(--color-lightblue-40);
  $border-width: 6px;

  :deep(.cms-p) {
    display: contents;
  }

  .highlighted-box {
    // Using inline-block to avoid margin collapsing
    display: inline-block;
    position: relative;
    border: $border-width solid $highlight-color;
    background: var(--color-white);
    margin: 26px 0 32px;

    @include vgs-media-mobile {
      margin-bottom: 24px;
    }

    &.auto {
      width: fit-content;
    }

    &.full {
      width: 100%;
    }

    .label {
      background-color: $highlight-color;
      position: absolute;
      translate: -$border-width -100%;
      margin: 0;
      padding: .1875rem 1.5rem;
      color: var(--color-white);
      font-size: 1.2rem;
      font-style: normal;
      font-weight: 500;
      line-height: 24px;
    }

    .content {
      margin: 0;
      padding: 3rem;
      color: var(--color-blue-50);
      font-size: 1.75rem;
      font-weight: 300;
      line-height: 170%; /* 47.6px */
      width: fit-content;

      &.left {
        text-align: left;
      }

      &.center {
        text-align: center;
        margin: 0 auto;
      }
    }
  }
</style>
